import React, { useEffect, useRef } from 'react';

export const SignaturePad = ({
  drawn,
  error,
  initialDataUrl,
  onChange
}: {
  drawn: boolean;
  error: string;
  initialDataUrl?: string;
  onChange: (drawn: boolean, dataUrl?: string) => void;
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;
    context.strokeStyle = '#000';
    context.lineWidth = 3;
    context.lineCap = 'round';
    context.lineJoin = 'round';

    if (initialDataUrl) {
      const image = new Image();
      image.onload = () => {
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
      };
      image.src = initialDataUrl;
    }
  }, [initialDataUrl]);

  const pointFromEvent = (event: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const touch = 'touches' in event ? event.touches[0] : undefined;
    const clientX = touch ? touch.clientX : (event as React.MouseEvent<HTMLCanvasElement>).clientX;
    const clientY = touch ? touch.clientY : (event as React.MouseEvent<HTMLCanvasElement>).clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const emitSignature = () => {
    const canvas = canvasRef.current;
    onChange(true, canvas?.toDataURL('image/png'));
  };

  const startDrawing = (event: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    event.preventDefault();
    drawingRef.current = true;
    lastPointRef.current = pointFromEvent(event);
  };

  const draw = (event: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    event.preventDefault();
    const context = canvasRef.current?.getContext('2d');
    if (!context) return;
    const point = pointFromEvent(event);
    context.beginPath();
    context.moveTo(lastPointRef.current.x, lastPointRef.current.y);
    context.lineTo(point.x, point.y);
    context.stroke();
    lastPointRef.current = point;
    emitSignature();
  };

  const stopDrawing = () => {
    drawingRef.current = false;
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (canvas && context) context.clearRect(0, 0, canvas.width, canvas.height);
    onChange(false, '');
  };

  return (
    <div className="form-group">
      <label>Assinatura do Responsável *</label>
      {!drawn && (
        <div className="signature-help">
          <span>Passe o dedo aqui para assinar</span>
        </div>
      )}
      <div className="signature-pad-container">
        <div className="canvas-wrapper">
          <canvas
            ref={canvasRef}
            id="signature-pad"
            width={350}
            height={120}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
          />
        </div>
        <div className="signature-buttons">
          <button type="button" className="btn-clear-signature" onClick={clear}>
            Limpar
          </button>
          <button type="button" className="btn-test-signature" onClick={() => onChange(drawn)}>
            Reinicializar
          </button>
        </div>
      </div>
      {error && <div className="error-message">{error}</div>}
    </div>
  );
};
